/**
 * FloorSelector.tsx
 * 층 선택 드롭다운 및 층 관리
 *
 * Features:
 * - 층 목록 드롭다운
 * - 좌석 수 표시
 * - 새 층 추가 모달
 * - 층 관리 모달 (수정/삭제)
 * - 층 순서 변경 (Drag & Drop - 향후 구현)
 */

import React, { useState, useEffect, Fragment } from 'react';
import { Menu, Transition, Dialog } from '@headlessui/react';
import { ChevronDownIcon, PlusIcon, Cog6ToothIcon, ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/outline';
import { apiClient } from '../../../../infrastructure/api';
import { useToast } from '../../../../contexts/ToastContextOptimized';

// ============================================================================
// Types & Interfaces
// ============================================================================

// 백엔드 Room API 응답 형식
interface Room {
  id: string;
  name: string;
  description?: string;
  room_type: string;
  capacity?: number;
  organization_id: string;
  grid_width: number;
  grid_height: number;
  room_layout?: Record<string, unknown>;
  display_order: number;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

// 프론트엔드 내부에서 사용하는 Floor 형식 (Room을 매핑)
export interface Floor {
  id: string;
  organization_id: string;
  name: string;
  background_image_url?: string;
  canvas_width: number;
  canvas_height: number;
  seat_count?: number;
  created_at?: string;
  updated_at?: string;
}

interface FloorCreate {
  name: string;
  canvas_width: number;
  canvas_height: number;
  background_image_url?: string;
}

interface FloorUpdate {
  name?: string;
  canvas_width?: number;
  canvas_height?: number;
  background_image_url?: string;
}

// Room → Floor 매핑 함수
const mapRoomToFloor = (room: Room): Floor => ({
  id: room.id,
  organization_id: room.organization_id,
  name: room.name,
  canvas_width: room.grid_width,
  canvas_height: room.grid_height,
  seat_count: room.capacity,
  created_at: room.created_at,
  updated_at: room.updated_at,
});

interface FloorSelectorProps {
  organizationId: string;
  selectedFloorId: string | null;
  onFloorChange: (floorId: string) => void;
  onFloorsLoaded?: (floors: Floor[]) => void;  // 룸 목록 로드 시 콜백
  showAllOption?: boolean;  // "전체" 옵션 표시 여부
  allowManage?: boolean;    // "새 룸" 버튼 + 룸 관리 메뉴 노출 여부 (기본 true)
}

// ============================================================================
// Main Component
// ============================================================================

export const FloorSelector: React.FC<FloorSelectorProps> = ({
  organizationId,
  selectedFloorId,
  onFloorChange,
  onFloorsLoaded,
  showAllOption = true,  // 기본값: 전체 옵션 표시
  allowManage = true,    // 기본값: 새 룸/룸 관리 메뉴 노출
}) => {
  const { showSuccess, showError, showWarning } = useToast();

  // ============================================================================
  // State
  // ============================================================================

  const [floors, setFloors] = useState<Floor[]>([]);
  const [loading, setLoading] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isManageModalOpen, setIsManageModalOpen] = useState(false);
  const [selectedFloor, setSelectedFloor] = useState<Floor | null>(null);

  // Form state for create/edit
  const [formData, setFormData] = useState<FloorCreate>({
    name: '',
    canvas_width: 1200,
    canvas_height: 800,
    background_image_url: '',
  });

  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});

  // ============================================================================
  // API Calls
  // ============================================================================

  const fetchFloors = async () => {
    setLoading(true);
    try {
      // rooms API 호출 후 Floor 형식으로 매핑
      const response = await apiClient.get<Room[]>('/api/v1/owner/rooms');
      const mappedFloors = response.data.map(mapRoomToFloor);
      setFloors(mappedFloors);

      // 부모 컴포넌트에 룸 목록 전달
      if (onFloorsLoaded) {
        onFloorsLoaded(mappedFloors);
      }

      // Auto-select: 전체 보기 (룸이 2개 이상) 또는 첫 번째 룸 (1개)
      if (!selectedFloorId && mappedFloors.length > 0) {
        if (showAllOption && mappedFloors.length > 1) {
          onFloorChange('all');
        } else {
          onFloorChange(mappedFloors[0].id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch rooms:', error);
      showError('룸 목록 조회 실패', '다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  const createFloor = async () => {
    // Validation
    const errors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      errors.name = '룸 이름을 입력해주세요.';
    }

    if (formData.canvas_width < 600 || formData.canvas_width > 3000) {
      errors.canvas_width = '너비는 600px ~ 3000px 사이여야 합니다.';
    }

    if (formData.canvas_height < 400 || formData.canvas_height > 2000) {
      errors.canvas_height = '높이는 400px ~ 2000px 사이여야 합니다.';
    }

    // Check duplicate name
    if (floors.some(f => f.name.toLowerCase() === formData.name.toLowerCase())) {
      errors.name = '이미 존재하는 룸 이름입니다.';
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    setLoading(true);
    try {
      // rooms API 형식으로 변환하여 전송
      const roomData = {
        name: formData.name,
        grid_width: formData.canvas_width,
        grid_height: formData.canvas_height,
        room_type: 'reading_room',
      };
      const response = await apiClient.post<Room>('/api/v1/owner/rooms', roomData);
      const newFloor = mapRoomToFloor(response.data);
      setFloors([...floors, newFloor]);
      setIsCreateModalOpen(false);
      resetForm();
      showSuccess('룸 생성 완료', `${formData.name}이 생성되었습니다.`);
      onFloorChange(newFloor.id);
    } catch (error: any) {
      console.error('Failed to create room:', error);
      if (error.response?.status === 409) {
        showError('룸 생성 실패', '이미 존재하는 룸 이름입니다.');
      } else {
        showError('룸 생성 실패', '다시 시도해주세요.');
      }
    } finally {
      setLoading(false);
    }
  };

  const updateFloor = async (floorId: string, data: FloorUpdate) => {
    setLoading(true);
    try {
      // rooms API 형식으로 변환
      const roomData: Record<string, unknown> = {};
      if (data.name) roomData.name = data.name;
      if (data.canvas_width) roomData.grid_width = data.canvas_width;
      if (data.canvas_height) roomData.grid_height = data.canvas_height;

      const response = await apiClient.put<Room>(`/api/v1/owner/rooms/${floorId}`, roomData);
      const updatedFloor = mapRoomToFloor(response.data);
      setFloors(floors.map(f => (f.id === floorId ? updatedFloor : f)));
      showSuccess('룸 수정 완료');
      setIsManageModalOpen(false);
    } catch (error) {
      console.error('Failed to update room:', error);
      showError('룸 수정 실패', '다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  const reorderRooms = async (roomIds: string[]) => {
    try {
      await apiClient.put('/api/v1/owner/rooms/reorder', { room_ids: roomIds });
    } catch (error) {
      console.error('Failed to reorder rooms:', error);
      showError('순서 변경 실패', '다시 시도해주세요.');
    }
  };

  const moveRoom = async (index: number, direction: 'up' | 'down') => {
    const newFloors = [...floors];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newFloors.length) return;

    [newFloors[index], newFloors[targetIndex]] = [newFloors[targetIndex], newFloors[index]];
    setFloors(newFloors);

    if (onFloorsLoaded) {
      onFloorsLoaded(newFloors);
    }

    await reorderRooms(newFloors.map(f => f.id));
    showSuccess('순서 변경 완료');
  };

  const deleteFloor = async (floorId: string) => {
    const floor = floors.find(f => f.id === floorId);
    if (!floor) return;

    // Confirm deletion
    const confirmed = window.confirm(
      `"${floor.name}"을 삭제하시겠습니까?\n\n` +
        `⚠️ 경고: 이 룸에 있는 모든 좌석도 함께 삭제됩니다.\n` +
        `(좌석 수: ${floor.seat_count || 0}개)`
    );

    if (!confirmed) return;

    setLoading(true);
    try {
      // rooms API는 DELETE 엔드포인트가 없으므로 is_active=false로 비활성화
      await apiClient.put(`/api/v1/owner/rooms/${floorId}`, { is_active: false });
      const updatedFloors = floors.filter(f => f.id !== floorId);
      setFloors(updatedFloors);
      showSuccess('룸 삭제 완료', `${floor.name}이 삭제되었습니다.`);

      // Select another floor if deleted was selected
      if (selectedFloorId === floorId && updatedFloors.length > 0) {
        onFloorChange(updatedFloors[0].id);
      }
      setIsManageModalOpen(false);
    } catch (error) {
      console.error('Failed to delete room:', error);
      showError('룸 삭제 실패', '다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // Effects
  // ============================================================================

  useEffect(() => {
    // rooms API는 인증 토큰에서 organization_id를 가져오므로 바로 호출
    fetchFloors();
  }, []);

  // ============================================================================
  // Helpers
  // ============================================================================

  const resetForm = () => {
    setFormData({
      name: '',
      canvas_width: 1200,
      canvas_height: 800,
      background_image_url: '',
    });
    setValidationErrors({});
  };

  const currentFloor = selectedFloorId === 'all' ? null : floors.find(f => f.id === selectedFloorId);
  const isAllSelected = selectedFloorId === 'all';
  const totalSeatCount = floors.reduce((sum, f) => sum + (f.seat_count || 0), 0);

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <div className="flex items-center gap-3">
      {/* Floor Dropdown */}
      <Menu as="div" className="relative inline-block text-left">
        <Menu.Button className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500">
          <span className="text-xl">{isAllSelected ? '📋' : '🏢'}</span>
          <span className="font-medium text-gray-700">
            {isAllSelected ? '전체 보기' : (currentFloor ? currentFloor.name : '룸 선택')}
          </span>
          {isAllSelected ? (
            <span className="text-sm text-gray-500">({totalSeatCount}석)</span>
          ) : currentFloor && currentFloor.seat_count !== undefined && (
            <span className="text-sm text-gray-500">({currentFloor.seat_count}석)</span>
          )}
          <ChevronDownIcon className="w-5 h-5 text-gray-400" />
        </Menu.Button>

        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="absolute left-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg focus:outline-none z-50">
            <div className="py-1">
              {/* 전체 보기 옵션 */}
              {showAllOption && floors.length > 1 && (
                <>
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={() => onFloorChange('all')}
                        className={`${
                          active ? 'bg-blue-50' : ''
                        } ${
                          isAllSelected ? 'bg-blue-100' : ''
                        } w-full text-left px-4 py-2 text-sm text-gray-700 flex items-center justify-between`}
                      >
                        <span className="flex items-center gap-2">
                          {isAllSelected && <span className="text-blue-600">✓</span>}
                          <span className="text-xl">📋</span>
                          <span className="font-medium">전체 보기</span>
                        </span>
                        <span className="text-xs text-gray-500">{totalSeatCount}석</span>
                      </button>
                    )}
                  </Menu.Item>
                  <div className="border-t border-gray-200 my-1"></div>
                </>
              )}

              {floors.map(floor => (
                <Menu.Item key={floor.id}>
                  {({ active }) => (
                    <button
                      onClick={() => onFloorChange(floor.id)}
                      className={`${
                        active ? 'bg-blue-50' : ''
                      } ${
                        selectedFloorId === floor.id ? 'bg-blue-100' : ''
                      } w-full text-left px-4 py-2 text-sm text-gray-700 flex items-center justify-between`}
                    >
                      <span className="flex items-center gap-2">
                        {selectedFloorId === floor.id && <span className="text-blue-600">✓</span>}
                        <span className="font-medium">{floor.name}</span>
                      </span>
                      {floor.seat_count !== undefined && (
                        <span className="text-xs text-gray-500">{floor.seat_count}석</span>
                      )}
                    </button>
                  )}
                </Menu.Item>
              ))}

              {floors.length === 0 && (
                <div className="px-4 py-3 text-sm text-gray-500 text-center">
                  룸이 없습니다
                </div>
              )}

              {allowManage && (
                <>
                  <div className="border-t border-gray-200 my-1"></div>
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={() => setIsManageModalOpen(true)}
                        className={`${
                          active ? 'bg-gray-50' : ''
                        } w-full text-left px-4 py-2 text-sm text-gray-700 flex items-center gap-2`}
                      >
                        <Cog6ToothIcon className="w-4 h-4" />
                        룸 관리
                      </button>
                    )}
                  </Menu.Item>
                </>
              )}
            </div>
          </Menu.Items>
        </Transition>
      </Menu>

      {/* New Floor Button — 모니터링 화면(allowManage=false)에서는 숨김 */}
      {allowManage && (
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow-sm transition-colors duration-150"
        >
          <PlusIcon className="w-5 h-5" />
          <span className="font-medium">새 룸</span>
        </button>
      )}

      {/* Create Floor Modal */}
      <Transition appear show={isCreateModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setIsCreateModalOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 mb-4">
                    새 룸 추가
                  </Dialog.Title>

                  <div className="space-y-4">
                    {/* Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        룸 이름 *
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={e => {
                          setFormData({ ...formData, name: e.target.value });
                          setValidationErrors({ ...validationErrors, name: '' });
                        }}
                        placeholder="예: A열람실, B자습실, 1층"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      {validationErrors.name && (
                        <p className="text-sm text-red-600 mt-1">{validationErrors.name}</p>
                      )}
                    </div>

                    {/* Width */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        캔버스 너비 (px) *
                      </label>
                      <input
                        type="number"
                        value={formData.canvas_width}
                        onChange={e => {
                          setFormData({ ...formData, canvas_width: parseInt(e.target.value) });
                          setValidationErrors({ ...validationErrors, canvas_width: '' });
                        }}
                        min={600}
                        max={3000}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">600 ~ 3000px</p>
                      {validationErrors.canvas_width && (
                        <p className="text-sm text-red-600 mt-1">{validationErrors.canvas_width}</p>
                      )}
                    </div>

                    {/* Height */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        캔버스 높이 (px) *
                      </label>
                      <input
                        type="number"
                        value={formData.canvas_height}
                        onChange={e => {
                          setFormData({ ...formData, canvas_height: parseInt(e.target.value) });
                          setValidationErrors({ ...validationErrors, canvas_height: '' });
                        }}
                        min={400}
                        max={2000}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">400 ~ 2000px</p>
                      {validationErrors.canvas_height && (
                        <p className="text-sm text-red-600 mt-1">
                          {validationErrors.canvas_height}
                        </p>
                      )}
                    </div>

                    {/* Background Image (Optional) */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        배경 이미지 URL (선택)
                      </label>
                      <input
                        type="text"
                        value={formData.background_image_url || ''}
                        onChange={e =>
                          setFormData({ ...formData, background_image_url: e.target.value })
                        }
                        placeholder="https://..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="mt-6 flex gap-3 justify-end">
                    <button
                      onClick={() => {
                        setIsCreateModalOpen(false);
                        resetForm();
                      }}
                      className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-150"
                    >
                      취소
                    </button>
                    <button
                      onClick={createFloor}
                      disabled={loading}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? '생성 중...' : '생성'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Manage Floors Modal */}
      <Transition appear show={isManageModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setIsManageModalOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 mb-4">
                    룸 관리
                  </Dialog.Title>

                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {floors.map((floor, index) => (
                      <div
                        key={floor.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-150"
                      >
                        {/* 순서 변경 버튼 */}
                        <div className="flex flex-col mr-2">
                          <button
                            onClick={() => moveRoom(index, 'up')}
                            disabled={loading || index === 0}
                            className="p-0.5 text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                            title="위로 이동"
                          >
                            <ArrowUpIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => moveRoom(index, 'down')}
                            disabled={loading || index === floors.length - 1}
                            className="p-0.5 text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                            title="아래로 이동"
                          >
                            <ArrowDownIcon className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{floor.name}</div>
                          <div className="text-sm text-gray-500">
                            {floor.canvas_width}×{floor.canvas_height}px
                            {floor.seat_count !== undefined && ` • ${floor.seat_count}석`}
                          </div>
                        </div>
                        <button
                          onClick={() => deleteFloor(floor.id)}
                          disabled={loading}
                          className="ml-3 px-3 py-1 text-sm font-medium text-red-600 hover:bg-red-50 rounded transition-colors duration-150 disabled:opacity-50"
                        >
                          삭제
                        </button>
                      </div>
                    ))}

                    {floors.length === 0 && (
                      <div className="text-center py-8 text-gray-500">룸이 없습니다</div>
                    )}
                  </div>

                  <div className="mt-6 flex justify-end">
                    <button
                      onClick={() => setIsManageModalOpen(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-150"
                    >
                      닫기
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default FloorSelector;
